/**
 * Turns chat attachment records (GET /attachments/chat-attachments/:id) into what a
 * message bubble shows: an image preview, or the file name for anything else.
 *
 * Kept free of React and the API client so it can be tested on its own; the hook
 * `useAttachmentDisplay` does the fetching.
 */

export interface AttachmentData {
  id: string;
  name: string;
  type: string;
  size: number;
  createdAt: string;
  file_url?: string; // For downloading/previewing
  content?: string; // A data URL for images up to 2 MB (inline display)
}

export interface AttachedFileDisplay {
  id: string;
  name: string;
  type: string;
  isImage: boolean;
  preview?: string;
  file_url?: string;
}

// The AI service stores the file extension ("png"); older rows hold a mime type.
const IMAGE_TYPES = [
  'image/png',
  'image/jpg',
  'image/jpeg',
  'image/gif',
  'image/bmp',
  'image/webp',
  'image/svg+xml',
  'png',
  'jpg',
  'jpeg',
  'jfif',
  'gif',
  'bmp',
  'webp',
  'svg',
];

export function toAttachmentDisplay(data: AttachmentData): AttachedFileDisplay {
  const type = (data.type || '').toLowerCase();
  return {
    id: data.id,
    name: data.name,
    type: data.type,
    isImage: type.startsWith('image/') || IMAGE_TYPES.includes(type),
    preview: data.file_url || data.content,
    file_url: data.file_url,
  };
}

/**
 * The displays for `ids`, in order. One that cannot be fetched (deleted, or no longer
 * the founder's) is left out rather than hiding the others; when none can be fetched,
 * the first error is thrown.
 */
export async function loadAttachmentDisplays(
  ids: string[],
  fetchOne: (id: string) => Promise<AttachmentData>
): Promise<AttachedFileDisplay[]> {
  const results = await Promise.allSettled(ids.map((id) => fetchOne(id)));
  const loaded = results
    .filter(
      (result): result is PromiseFulfilledResult<AttachmentData> =>
        result.status === 'fulfilled' && Boolean(result.value)
    )
    .map((result) => toAttachmentDisplay(result.value));
  if (loaded.length === 0) {
    const firstFailure = results.find(
      (result): result is PromiseRejectedResult => result.status === 'rejected'
    );
    if (firstFailure) throw firstFailure.reason;
  }
  return loaded;
}
